import {
  type OverlayLayout,
  overlayLayoutBox,
  type SceneLayout,
  sceneLayoutBoxes,
} from "@cva/shared";
import { cn } from "../../lib/utils";

/**
 * Small pictures of layouts: the frame with its areas. Used by the layout
 * tab, the clip properties and the timeline's context menu.
 */

/** A screen layout ("Bildaufteilung"): the areas numbered, 1 = the main track clip. */
export function LayoutPicture({
  layout,
  frame,
  spaced,
  size = 64,
}: {
  layout: SceneLayout;
  frame: { width: number; height: number };
  spaced: boolean;
  /** Longer side in px. */
  size?: number;
}) {
  const inset = spaced || Boolean(layout.framed);
  const slots = sceneLayoutBoxes(layout.id, frame, { spaced: inset });
  const scale = Math.min(size / frame.width, size / frame.height);
  return (
    <span
      className="relative block shrink-0 rounded-[3px] bg-zinc-300"
      style={{ width: frame.width * scale, height: frame.height * scale }}
    >
      {slots.map((slot, index) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: slots have no identity
          key={index}
          className={cn(
            "absolute flex items-center justify-center font-semibold text-white",
            size >= 48 ? "text-[9px]" : "text-[7px]",
            index === 0 ? "bg-brand-600" : "bg-brand-400",
          )}
          style={{
            left: slot.box.x * scale,
            top: slot.box.y * scale,
            width: slot.box.width * scale,
            height: slot.box.height * scale,
            borderRadius: Math.max(1, slot.radius * scale),
            outline: index > 0 && !inset ? "1px solid rgb(255 255 255 / 0.7)" : undefined,
          }}
        >
          {slots.length > 1 ? index + 1 : null}
        </span>
      ))}
    </span>
  );
}

/** A position preset of a clip on a track above ("Anordnung"): the frame with the box in it. */
export function OverlayLayoutPicture({
  layout,
  frame,
  source,
  height = 26,
}: {
  layout: OverlayLayout;
  frame: { width: number; height: number };
  source: { width: number; height: number };
  height?: number;
}) {
  const box = overlayLayoutBox(layout, frame, source);
  const scale = height / frame.height;
  return (
    <span
      className="relative block shrink-0 rounded-[2px] bg-zinc-200"
      style={{ width: frame.width * scale, height: frame.height * scale }}
    >
      <span
        className="absolute rounded-[1px] bg-brand-500"
        style={{
          left: box.x * scale,
          top: box.y * scale,
          width: box.width * scale,
          height: box.height * scale,
        }}
      />
    </span>
  );
}
